import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, TextField, Box, Alert, CircularProgress, Paper, Divider, Chip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { config } from '../config/env';

interface CvShareData {
  optimizedShortProfile?: string;
  optimizedWorkExperiences?: any[];
  optimizedCvText?: string;
  relevantItSkills?: any[];
  relevantLanguages?: any[];
  relevantCertifications?: any[];
  relevantCoreCompetencies?: any[];
  relevantAdditionalInfo?: string;
  relevantHobbies?: any[];
  relevantIndustries?: any[];
  relevantEducations?: any[];
  itSkills?: any[];
  languages?: any[];
  certifications?: any[];
  coreCompetencies?: any[];
  educations?: any[];
  workExperience?: any[];
  applicationId?: string;
  expiresAt?: string;
  status?: string;
}

const CvSharePage: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cvData, setCvData] = useState<CvShareData | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  
  // Formular-States
  const [optimizedShortProfile, setOptimizedShortProfile] = useState('');
  const [relevantItSkills, setRelevantItSkills] = useState<any[]>([]);
  const [newItSkillName, setNewItSkillName] = useState<string>('');
  const [newItSkillLevel, setNewItSkillLevel] = useState<string>('');
  const [editingItSkillIndex, setEditingItSkillIndex] = useState<number | null>(null);
  const [relevantLanguages, setRelevantLanguages] = useState<any[]>([]);
  const [newLanguageName, setNewLanguageName] = useState<string>('');
  const [newLanguageLevel, setNewLanguageLevel] = useState<string>('');
  const [editingLanguageIndex, setEditingLanguageIndex] = useState<number | null>(null);
  const [relevantCertifications, setRelevantCertifications] = useState<any[]>([]);
  const [newCertificationName, setNewCertificationName] = useState<string>('');
  const [newCertificationIssuer, setNewCertificationIssuer] = useState<string>('');
  const [newCertificationDate, setNewCertificationDate] = useState<string>('');
  const [editingCertificationIndex, setEditingCertificationIndex] = useState<number | null>(null);
  const [relevantCoreCompetencies, setRelevantCoreCompetencies] = useState<any[]>([]);
  const [newCoreCompetency, setNewCoreCompetency] = useState<string>('');
  const [editingCoreCompetencyIndex, setEditingCoreCompetencyIndex] = useState<number | null>(null);
  const [relevantAdditionalInfo, setRelevantAdditionalInfo] = useState<Array<{ info: string; details: string }>>([]);
  const [newAdditionalInfoInfo, setNewAdditionalInfoInfo] = useState<string>('');
  const [newAdditionalInfoDetails, setNewAdditionalInfoDetails] = useState<string>('');
  const [editingAdditionalInfoIndex, setEditingAdditionalInfoIndex] = useState<number | null>(null);
  const [relevantHobbies, setRelevantHobbies] = useState<any[]>([]);
  const [newHobbyName, setNewHobbyName] = useState<string>('');
  const [newHobbyDescription, setNewHobbyDescription] = useState<string>('');
  const [editingHobbyIndex, setEditingHobbyIndex] = useState<number | null>(null);
  const [optimizedWorkExperiences, setOptimizedWorkExperiences] = useState<any[]>([]);
  const [relevantEducations, setRelevantEducations] = useState<any[]>([]);
  const [newEducationDegree, setNewEducationDegree] = useState<string>('');
  const [newEducationInstitution, setNewEducationInstitution] = useState<string>('');
  const [newEducationStartDate, setNewEducationStartDate] = useState<string>('');
  const [newEducationEndDate, setNewEducationEndDate] = useState<string>('');
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
  const [relevantIndustries, setRelevantIndustries] = useState<any[]>([]);
  const [newIndustryName, setNewIndustryName] = useState<string>('');
  const [newIndustryYears, setNewIndustryYears] = useState<string>('');
  const [editingIndustryIndex, setEditingIndustryIndex] = useState<number | null>(null);
  
  useEffect(() => {
    loadCvData();
  }, [token]);
  
  const loadCvData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔗 Lade CV-Daten für Token:', token);
      console.log('🔗 API URL:', `${config.backend.baseUrl}/api/cv-share/${token}`);
      
      const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}`);
      
      console.log('📡 Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Fehler:', errorData);
        if (response.status === 410) {
          setIsExpired(true);
          setError('Dieser Link ist abgelaufen.');
        } else {
          setError(errorData.error || 'Fehler beim Laden der CV-Daten');
        }
        return;
      }
      
      const result = await response.json();
      console.log('✅ API Response:', result);
      
      if (result.success && result.data) {
        const data = result.data;
        console.log('📊 CV-Daten (vollständig):', JSON.stringify(data, null, 2));
        console.log('📊 ApplicationId aus Response:', data.applicationId);
        console.log('📊 Optimierte Felder vorhanden:', {
          optimizedShortProfile: !!data.optimizedShortProfile,
          optimizedShortProfileLength: data.optimizedShortProfile?.length || 0,
          optimizedWorkExperiences: !!data.optimizedWorkExperiences,
          optimizedWorkExperiencesCount: data.optimizedWorkExperiences?.length || 0,
          relevantItSkills: !!data.relevantItSkills,
          relevantItSkillsCount: data.relevantItSkills?.length || 0,
          relevantLanguages: !!data.relevantLanguages,
          relevantLanguagesCount: data.relevantLanguages?.length || 0,
          relevantAdditionalInfo: !!data.relevantAdditionalInfo,
          relevantAdditionalInfoLength: data.relevantAdditionalInfo?.length || 0,
          relevantCertifications: !!data.relevantCertifications,
          relevantCoreCompetencies: !!data.relevantCoreCompetencies,
          relevantHobbies: !!data.relevantHobbies,
          relevantEducations: !!data.relevantEducations
        });
        
        setCvData(data);
        
        // Setze Formular-Werte - NUR optimierte/relevante Felder (keine Fallbacks auf nicht-optimierte)
        const shortProfile = data.optimizedShortProfile || '';
        // Normalisiere IT-Skills: Stelle sicher, dass sie als Array von Objekten mit 'skill'/'name' und 'level' Feldern vorliegen
        let itSkills: any[] = [];
        if (Array.isArray(data.relevantItSkills)) {
          itSkills = data.relevantItSkills.map((skill: any) => {
            if (typeof skill === 'string') {
              return { skill: skill, name: skill, level: '', proficiency: '' };
            } else if (skill && typeof skill === 'object') {
              return {
                skill: skill.skill || skill.name || '',
                name: skill.name || skill.skill || '',
                level: skill.level || skill.proficiency || '',
                proficiency: skill.proficiency || skill.level || ''
              };
            }
            return { skill: '', name: '', level: '', proficiency: '' };
          }).filter((skill: any) => skill.skill || skill.name);
        }
        // Normalisiere Sprachen: Stelle sicher, dass sie als Array von Objekten mit 'language'/'name' und 'level' Feldern vorliegen
        let languages: any[] = [];
        if (Array.isArray(data.relevantLanguages)) {
          languages = data.relevantLanguages.map((lang: any) => {
            if (typeof lang === 'string') {
              return { language: lang, name: lang, level: '', proficiency: '' };
            } else if (lang && typeof lang === 'object') {
              return {
                language: lang.language || lang.name || '',
                name: lang.name || lang.language || '',
                level: lang.level || lang.proficiency || '',
                proficiency: lang.proficiency || lang.level || ''
              };
            }
            return { language: '', name: '', level: '', proficiency: '' };
          }).filter((lang: any) => lang.language || lang.name);
        }
        // Normalisiere Zertifikate: Stelle sicher, dass sie als Array von Objekten mit 'name'/'certificate', 'issuer' und 'date' Feldern vorliegen
        let certifications: any[] = [];
        if (Array.isArray(data.relevantCertifications)) {
          certifications = data.relevantCertifications.map((cert: any) => {
            if (typeof cert === 'string') {
              return { name: cert, certificate: cert, issuer: '', date: '' };
            } else if (cert && typeof cert === 'object') {
              return {
                name: cert.name || cert.certificate || '',
                certificate: cert.certificate || cert.name || '',
                issuer: cert.issuer || '',
                date: cert.date || ''
              };
            }
            return { name: '', certificate: '', issuer: '', date: '' };
          }).filter((cert: any) => cert.name || cert.certificate);
        }
        // Normalisiere Kernkompetenzen: Stelle sicher, dass sie als Array von Objekten mit 'competency' Feld vorliegen
        let coreCompetencies: any[] = [];
        if (Array.isArray(data.relevantCoreCompetencies)) {
          coreCompetencies = data.relevantCoreCompetencies.map((comp: any) => {
            if (typeof comp === 'string') {
              return { competency: comp };
            } else if (comp && typeof comp === 'object') {
              return { competency: comp.competency || comp.name || '' };
            }
            return { competency: '' };
          }).filter((comp: any) => comp.competency);
        }
        // Normalisiere zusätzliche Informationen: Stelle sicher, dass sie als Array von Objekten mit 'info' und 'details' Feldern vorliegen
        let additionalInfo: Array<{ info: string; details: string }> = [];
        if (Array.isArray(data.relevantAdditionalInfo)) {
          additionalInfo = data.relevantAdditionalInfo.map((item: any) => {
            if (typeof item === 'string') {
              // Wenn es ein String ist, versuche es zu parsen (Format: "info: details")
              if (item.includes(':')) {
                const [info, ...detailsParts] = item.split(':');
                return { info: info.trim(), details: detailsParts.join(':').trim() };
              }
              return { info: item.trim(), details: '' };
            } else if (item && typeof item === 'object') {
              return {
                info: item.info || '',
                details: item.details || ''
              };
            }
            return { info: '', details: '' };
          }).filter((item: any) => item.info);
        } else if (typeof data.relevantAdditionalInfo === 'string' && data.relevantAdditionalInfo.trim()) {
          // Wenn es ein String ist, versuche es zu parsen
          const lines = data.relevantAdditionalInfo.split('\n').filter((line: string) => line.trim());
          additionalInfo = lines.map((line: string) => {
            if (line.includes(':')) {
              const [info, ...detailsParts] = line.split(':');
              return { info: info.trim(), details: detailsParts.join(':').trim() };
            }
            return { info: line.trim(), details: '' };
          });
        }
        // Normalisiere Hobbys: Stelle sicher, dass sie als Array von Objekten mit 'hobby'/'name' und 'description' Feldern vorliegen
        let hobbies: any[] = [];
        if (Array.isArray(data.relevantHobbies)) {
          hobbies = data.relevantHobbies.map((hobby: any) => {
            if (typeof hobby === 'string') {
              return { hobby: hobby, name: hobby, description: '' };
            } else if (hobby && typeof hobby === 'object') {
              return {
                hobby: hobby.hobby || hobby.name || '',
                name: hobby.name || hobby.hobby || '',
                description: hobby.description || ''
              };
            }
            return { hobby: '', name: '', description: '' };
          }).filter((hobby: any) => hobby.hobby || hobby.name);
        }
        const workExperiences = Array.isArray(data.optimizedWorkExperiences) ? data.optimizedWorkExperiences : [];
        // Normalisiere Ausbildungen: Stelle sicher, dass sie als Array von Objekten mit 'degree'/'title', 'institution'/'school', 'startDate' und 'endDate' Feldern vorliegen
        let educations: any[] = [];
        if (Array.isArray(data.relevantEducations)) {
          educations = data.relevantEducations.map((edu: any) => {
            if (typeof edu === 'string') {
              return { degree: edu, title: edu, institution: '', school: '', startDate: '', endDate: '' };
            } else if (edu && typeof edu === 'object') {
              return {
                degree: edu.degree || edu.title || '',
                title: edu.title || edu.degree || '',
                institution: edu.institution || edu.school || '',
                school: edu.school || edu.institution || '',
                startDate: edu.startDate || '',
                endDate: edu.endDate || ''
              };
            }
            return { degree: '', title: '', institution: '', school: '', startDate: '', endDate: '' };
          }).filter((edu: any) => edu.degree || edu.title);
        }
        // Normalisiere Branchen: Stelle sicher, dass sie als Array von Objekten mit 'industry'/'name' und 'years' Feldern vorliegen
        let industries: any[] = [];
        if (Array.isArray(data.relevantIndustries)) {
          industries = data.relevantIndustries.map((industry: any) => {
            if (typeof industry === 'string') {
              return { industry: industry, name: industry, years: '' };
            } else if (industry && typeof industry === 'object') {
              return {
                industry: industry.industry || industry.name || '',
                name: industry.name || industry.industry || '',
                years: industry.years || ''
              };
            }
            return { industry: '', name: '', years: '' };
          }).filter((industry: any) => industry.industry || industry.name);
        }
        
        console.log('📝 Setze Formular-Werte:', {
          applicationId: data.applicationId,
          shortProfile: shortProfile.substring(0, 50) + (shortProfile.length > 50 ? '...' : ''),
          shortProfileLength: shortProfile.length,
          shortProfileRaw: data.optimizedShortProfile,
          itSkillsCount: itSkills.length,
          languagesCount: languages.length,
          workExperiencesCount: workExperiences.length,
          additionalInfoLength: additionalInfo.length,
          coreCompetenciesCount: coreCompetencies.length,
          industriesCount: industries.length
        });
        
        setOptimizedShortProfile(shortProfile);
        setRelevantItSkills(itSkills);
        setRelevantLanguages(languages);
        setRelevantCertifications(certifications);
        setRelevantCoreCompetencies(coreCompetencies);
        setRelevantAdditionalInfo(additionalInfo);
        setRelevantHobbies(hobbies);
        setOptimizedWorkExperiences(workExperiences);
        setRelevantEducations(educations);
        setRelevantIndustries(industries);
        
        if (data.expiresAt) {
          setExpiresAt(data.expiresAt);
          const expires = new Date(data.expiresAt);
          if (new Date() > expires) {
            setIsExpired(true);
          }
        }
      } else {
        console.warn('⚠️ Keine CV-Daten in Response:', result);
        setError('Keine CV-Daten gefunden');
      }
    } catch (err: any) {
      console.error('❌ Fehler beim Laden der CV-Daten:', err);
      setError('Fehler beim Laden der CV-Daten: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handler zum Entfernen einer Kernkompetenz
  const handleDeleteCoreCompetency = (index: number) => {
    setRelevantCoreCompetencies(prev => prev.filter((_, i) => i !== index));
    setEditingCoreCompetencyIndex(null);
  };
  
  // Handler zum Bearbeiten einer Kernkompetenz
  const handleEditCoreCompetency = (index: number, value: string) => {
    setRelevantCoreCompetencies(prev => prev.map((comp, i) => 
      i === index ? { competency: value } : comp
    ));
  };
  
  // Handler zum Hinzufügen einer neuen Kernkompetenz
  const handleAddCoreCompetency = () => {
    if (newCoreCompetency.trim()) {
      setRelevantCoreCompetencies(prev => [...prev, { competency: newCoreCompetency.trim() }]);
      setNewCoreCompetency('');
    }
  };
  
  // Handler zum Entfernen eines IT-Skills
  const handleDeleteItSkill = (index: number) => {
    setRelevantItSkills(prev => prev.filter((_, i) => i !== index));
    setEditingItSkillIndex(null);
  };
  
  // Handler zum Bearbeiten eines IT-Skills
  const handleEditItSkill = (index: number, field: 'skill' | 'name' | 'level' | 'proficiency', value: string) => {
    setRelevantItSkills(prev => prev.map((skill, i) => {
      if (i === index) {
        const updated = { ...skill };
        if (field === 'skill' || field === 'name') {
          updated.skill = value;
          updated.name = value;
        } else if (field === 'level' || field === 'proficiency') {
          updated.level = value;
          updated.proficiency = value;
        }
        return updated;
      }
      return skill;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen IT-Skills
  const handleAddItSkill = () => {
    if (newItSkillName.trim()) {
      setRelevantItSkills(prev => [...prev, { 
        skill: newItSkillName.trim(), 
        name: newItSkillName.trim(),
        level: newItSkillLevel.trim() || '',
        proficiency: newItSkillLevel.trim() || ''
      }]);
      setNewItSkillName('');
      setNewItSkillLevel('');
    }
  };
  
  // Handler zum Entfernen einer Sprache
  const handleDeleteLanguage = (index: number) => {
    setRelevantLanguages(prev => prev.filter((_, i) => i !== index));
    setEditingLanguageIndex(null);
  };
  
  // Handler zum Bearbeiten einer Sprache
  const handleEditLanguage = (index: number, field: 'language' | 'name' | 'level' | 'proficiency', value: string) => {
    setRelevantLanguages(prev => prev.map((lang, i) => {
      if (i === index) {
        const updated = { ...lang };
        if (field === 'language' || field === 'name') {
          updated.language = value;
          updated.name = value;
        } else if (field === 'level' || field === 'proficiency') {
          updated.level = value;
          updated.proficiency = value;
        }
        return updated;
      }
      return lang;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Sprache
  const handleAddLanguage = () => {
    if (newLanguageName.trim()) {
      setRelevantLanguages(prev => [...prev, { 
        language: newLanguageName.trim(), 
        name: newLanguageName.trim(),
        level: newLanguageLevel.trim() || '',
        proficiency: newLanguageLevel.trim() || ''
      }]);
      setNewLanguageName('');
      setNewLanguageLevel('');
    }
  };
  
  // Handler zum Entfernen einer Branche
  const handleDeleteIndustry = (index: number) => {
    setRelevantIndustries(prev => prev.filter((_, i) => i !== index));
    setEditingIndustryIndex(null);
  };
  
  // Handler zum Bearbeiten einer Branche
  const handleEditIndustry = (index: number, field: 'industry' | 'name' | 'years', value: string) => {
    setRelevantIndustries(prev => prev.map((industry, i) => {
      if (i === index) {
        const updated = { ...industry };
        if (field === 'industry' || field === 'name') {
          updated.industry = value;
          updated.name = value;
        } else if (field === 'years') {
          updated.years = value;
        }
        return updated;
      }
      return industry;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Branche
  const handleAddIndustry = () => {
    if (newIndustryName.trim()) {
      setRelevantIndustries(prev => [...prev, { 
        industry: newIndustryName.trim(), 
        name: newIndustryName.trim(),
        years: newIndustryYears.trim() || ''
      }]);
      setNewIndustryName('');
      setNewIndustryYears('');
    }
  };
  
  // Handler zum Entfernen einer Ausbildung
  const handleDeleteEducation = (index: number) => {
    setRelevantEducations(prev => prev.filter((_, i) => i !== index));
    setEditingEducationIndex(null);
  };
  
  // Handler zum Bearbeiten einer Ausbildung
  const handleEditEducation = (index: number, field: 'degree' | 'title' | 'institution' | 'school' | 'startDate' | 'endDate', value: string) => {
    setRelevantEducations(prev => prev.map((edu, i) => {
      if (i === index) {
        const updated = { ...edu };
        if (field === 'degree' || field === 'title') {
          updated.degree = value;
          updated.title = value;
        } else if (field === 'institution' || field === 'school') {
          updated.institution = value;
          updated.school = value;
        } else if (field === 'startDate') {
          updated.startDate = value;
        } else if (field === 'endDate') {
          updated.endDate = value;
        }
        return updated;
      }
      return edu;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Ausbildung
  const handleAddEducation = () => {
    if (newEducationDegree.trim()) {
      setRelevantEducations(prev => [...prev, { 
        degree: newEducationDegree.trim(),
        title: newEducationDegree.trim(),
        institution: newEducationInstitution.trim() || '',
        school: newEducationInstitution.trim() || '',
        startDate: newEducationStartDate.trim() || '',
        endDate: newEducationEndDate.trim() || ''
      }]);
      setNewEducationDegree('');
      setNewEducationInstitution('');
      setNewEducationStartDate('');
      setNewEducationEndDate('');
    }
  };
  
  // Handler zum Entfernen eines Zertifikats
  const handleDeleteCertification = (index: number) => {
    setRelevantCertifications(prev => prev.filter((_, i) => i !== index));
    setEditingCertificationIndex(null);
  };
  
  // Handler zum Bearbeiten eines Zertifikats
  const handleEditCertification = (index: number, field: 'name' | 'certificate' | 'issuer' | 'date', value: string) => {
    setRelevantCertifications(prev => prev.map((cert, i) => {
      if (i === index) {
        const updated = { ...cert };
        if (field === 'name' || field === 'certificate') {
          updated.name = value;
          updated.certificate = value;
        } else if (field === 'issuer') {
          updated.issuer = value;
        } else if (field === 'date') {
          updated.date = value;
        }
        return updated;
      }
      return cert;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen Zertifikats
  const handleAddCertification = () => {
    if (newCertificationName.trim()) {
      setRelevantCertifications(prev => [...prev, { 
        name: newCertificationName.trim(),
        certificate: newCertificationName.trim(),
        issuer: newCertificationIssuer.trim() || '',
        date: newCertificationDate.trim() || ''
      }]);
      setNewCertificationName('');
      setNewCertificationIssuer('');
      setNewCertificationDate('');
    }
  };
  
  // Handler zum Entfernen eines Hobbys
  const handleDeleteHobby = (index: number) => {
    setRelevantHobbies(prev => prev.filter((_, i) => i !== index));
    setEditingHobbyIndex(null);
  };
  
  // Handler zum Bearbeiten eines Hobbys
  const handleEditHobby = (index: number, field: 'hobby' | 'name' | 'description', value: string) => {
    setRelevantHobbies(prev => prev.map((hobby, i) => {
      if (i === index) {
        const updated = { ...hobby };
        if (field === 'hobby' || field === 'name') {
          updated.hobby = value;
          updated.name = value;
        } else if (field === 'description') {
          updated.description = value;
        }
        return updated;
      }
      return hobby;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen Hobbys
  const handleAddHobby = () => {
    if (newHobbyName.trim()) {
      setRelevantHobbies(prev => [...prev, { 
        hobby: newHobbyName.trim(),
        name: newHobbyName.trim(),
        description: newHobbyDescription.trim() || ''
      }]);
      setNewHobbyName('');
      setNewHobbyDescription('');
    }
  };
  
  // Handler zum Entfernen einer zusätzlichen Information
  const handleDeleteAdditionalInfo = (index: number) => {
    setRelevantAdditionalInfo(prev => prev.filter((_, i) => i !== index));
    setEditingAdditionalInfoIndex(null);
  };
  
  // Handler zum Bearbeiten einer zusätzlichen Information
  const handleEditAdditionalInfo = (index: number, field: 'info' | 'details', value: string) => {
    setRelevantAdditionalInfo(prev => prev.map((info, i) => 
      i === index ? { ...info, [field]: value } : info
    ));
  };
  
  // Handler zum Hinzufügen einer neuen zusätzlichen Information
  const handleAddAdditionalInfo = () => {
    if (newAdditionalInfoInfo.trim()) {
      setRelevantAdditionalInfo(prev => [...prev, { 
        info: newAdditionalInfoInfo.trim(),
        details: newAdditionalInfoDetails.trim() || ''
      }]);
      setNewAdditionalInfoInfo('');
      setNewAdditionalInfoDetails('');
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const updateData: any = {};
      
      if (optimizedShortProfile !== cvData?.optimizedShortProfile) {
        updateData.optimizedShortProfile = optimizedShortProfile;
      }
      // Normalisiere IT-Skills für den Vergleich
      const normalizedCurrentItSkills = relevantItSkills.map((skill: any) => ({
        skill: typeof skill === 'string' ? skill : (skill?.skill || skill?.name || ''),
        name: typeof skill === 'string' ? skill : (skill?.name || skill?.skill || ''),
        level: typeof skill === 'object' ? (skill?.level || skill?.proficiency || '') : '',
        proficiency: typeof skill === 'object' ? (skill?.proficiency || skill?.level || '') : ''
      })).filter((skill: any) => skill.skill || skill.name);
      
      const normalizedOriginalItSkills = (cvData?.relevantItSkills || []).map((skill: any) => ({
        skill: typeof skill === 'string' ? skill : (skill?.skill || skill?.name || ''),
        name: typeof skill === 'string' ? skill : (skill?.name || skill?.skill || ''),
        level: typeof skill === 'object' ? (skill?.level || skill?.proficiency || '') : '',
        proficiency: typeof skill === 'object' ? (skill?.proficiency || skill?.level || '') : ''
      })).filter((skill: any) => skill.skill || skill.name);
      
      if (JSON.stringify(normalizedCurrentItSkills) !== JSON.stringify(normalizedOriginalItSkills)) {
        updateData.relevantItSkills = normalizedCurrentItSkills;
      }
      // Normalisiere Sprachen für den Vergleich
      const normalizedCurrentLanguages = relevantLanguages.map((lang: any) => ({
        language: typeof lang === 'string' ? lang : (lang?.language || lang?.name || ''),
        name: typeof lang === 'string' ? lang : (lang?.name || lang?.language || ''),
        level: typeof lang === 'object' ? (lang?.level || lang?.proficiency || '') : '',
        proficiency: typeof lang === 'object' ? (lang?.proficiency || lang?.level || '') : ''
      })).filter((lang: any) => lang.language || lang.name);
      
      const normalizedOriginalLanguages = (cvData?.relevantLanguages || []).map((lang: any) => ({
        language: typeof lang === 'string' ? lang : (lang?.language || lang?.name || ''),
        name: typeof lang === 'string' ? lang : (lang?.name || lang?.language || ''),
        level: typeof lang === 'object' ? (lang?.level || lang?.proficiency || '') : '',
        proficiency: typeof lang === 'object' ? (lang?.proficiency || lang?.level || '') : ''
      })).filter((lang: any) => lang.language || lang.name);
      
      if (JSON.stringify(normalizedCurrentLanguages) !== JSON.stringify(normalizedOriginalLanguages)) {
        updateData.relevantLanguages = normalizedCurrentLanguages;
      }
      // Normalisiere Zertifikate für den Vergleich
      const normalizedCurrentCertifications = relevantCertifications.map((cert: any) => ({
        name: typeof cert === 'string' ? cert : (cert?.name || cert?.certificate || ''),
        certificate: typeof cert === 'string' ? cert : (cert?.certificate || cert?.name || ''),
        issuer: typeof cert === 'object' ? (cert?.issuer || '') : '',
        date: typeof cert === 'object' ? (cert?.date || '') : ''
      })).filter((cert: any) => cert.name || cert.certificate);
      
      const normalizedOriginalCertifications = (cvData?.relevantCertifications || []).map((cert: any) => ({
        name: typeof cert === 'string' ? cert : (cert?.name || cert?.certificate || ''),
        certificate: typeof cert === 'string' ? cert : (cert?.certificate || cert?.name || ''),
        issuer: typeof cert === 'object' ? (cert?.issuer || '') : '',
        date: typeof cert === 'object' ? (cert?.date || '') : ''
      })).filter((cert: any) => cert.name || cert.certificate);
      
      if (JSON.stringify(normalizedCurrentCertifications) !== JSON.stringify(normalizedOriginalCertifications)) {
        updateData.relevantCertifications = normalizedCurrentCertifications;
      }
      // Normalisiere Kernkompetenzen für den Vergleich
      const normalizedCurrentCoreCompetencies = relevantCoreCompetencies.map((comp: any) => ({
        competency: typeof comp === 'string' ? comp : (comp?.competency || comp?.name || '')
      })).filter((comp: any) => comp.competency);
      
      const normalizedOriginalCoreCompetencies = (cvData?.relevantCoreCompetencies || []).map((comp: any) => ({
        competency: typeof comp === 'string' ? comp : (comp?.competency || comp?.name || '')
      })).filter((comp: any) => comp.competency);
      
      if (JSON.stringify(normalizedCurrentCoreCompetencies) !== JSON.stringify(normalizedOriginalCoreCompetencies)) {
        updateData.relevantCoreCompetencies = normalizedCurrentCoreCompetencies;
      }
      // Normalisiere zusätzliche Informationen für den Vergleich
      const normalizedCurrentAdditionalInfo = relevantAdditionalInfo.map((info: any) => ({
        info: typeof info === 'string' ? info : (info?.info || ''),
        details: typeof info === 'object' ? (info?.details || '') : ''
      })).filter((info: any) => info.info);
      
      const originalAdditionalInfo = cvData?.relevantAdditionalInfo;
      let normalizedOriginalAdditionalInfo: Array<{ info: string; details: string }> = [];
      
      if (originalAdditionalInfo) {
        if (Array.isArray(originalAdditionalInfo)) {
          normalizedOriginalAdditionalInfo = originalAdditionalInfo.map((info: any) => ({
            info: typeof info === 'string' ? info : (info?.info || ''),
            details: typeof info === 'object' ? (info?.details || '') : ''
          })).filter((info: any) => info.info);
        } else if (typeof originalAdditionalInfo === 'string' && originalAdditionalInfo.trim()) {
          const lines = originalAdditionalInfo.split('\n').filter((line: string) => line.trim());
          normalizedOriginalAdditionalInfo = lines.map((line: string) => {
            if (line.includes(':')) {
              const [info, ...detailsParts] = line.split(':');
              return { info: info.trim(), details: detailsParts.join(':').trim() };
            }
            return { info: line.trim(), details: '' };
          });
        }
      }
      
      if (JSON.stringify(normalizedCurrentAdditionalInfo) !== JSON.stringify(normalizedOriginalAdditionalInfo)) {
        updateData.relevantAdditionalInfo = normalizedCurrentAdditionalInfo;
      }
      // Normalisiere Hobbys für den Vergleich
      const normalizedCurrentHobbies = relevantHobbies.map((hobby: any) => ({
        hobby: typeof hobby === 'string' ? hobby : (hobby?.hobby || hobby?.name || ''),
        name: typeof hobby === 'string' ? hobby : (hobby?.name || hobby?.hobby || ''),
        description: typeof hobby === 'object' ? (hobby?.description || '') : ''
      })).filter((hobby: any) => hobby.hobby || hobby.name);
      
      const normalizedOriginalHobbies = (cvData?.relevantHobbies || []).map((hobby: any) => ({
        hobby: typeof hobby === 'string' ? hobby : (hobby?.hobby || hobby?.name || ''),
        name: typeof hobby === 'string' ? hobby : (hobby?.name || hobby?.hobby || ''),
        description: typeof hobby === 'object' ? (hobby?.description || '') : ''
      })).filter((hobby: any) => hobby.hobby || hobby.name);
      
      if (JSON.stringify(normalizedCurrentHobbies) !== JSON.stringify(normalizedOriginalHobbies)) {
        updateData.relevantHobbies = normalizedCurrentHobbies;
      }
      // Vergleich für Berufserfahrungen - normalisiere die Daten zuerst
      const normalizedCurrent = (optimizedWorkExperiences || []).map(exp => ({
        position: exp.position || exp.title || '',
        company: exp.company || exp.employer || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        description: exp.description || ''
      }));
      const normalizedOriginal = (cvData?.optimizedWorkExperiences || []).map(exp => ({
        position: exp.position || exp.title || '',
        company: exp.company || exp.employer || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        description: exp.description || ''
      }));
      
      if (JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal)) {
        console.log('📝 Berufserfahrungen haben sich geändert:', {
          current: normalizedCurrent,
          original: normalizedOriginal
        });
        updateData.optimizedWorkExperiences = optimizedWorkExperiences;
      }
      // Normalisiere Ausbildungen für den Vergleich
      const normalizedCurrentEducations = relevantEducations.map((edu: any) => ({
        degree: typeof edu === 'string' ? edu : (edu?.degree || edu?.title || ''),
        title: typeof edu === 'string' ? edu : (edu?.title || edu?.degree || ''),
        institution: typeof edu === 'object' ? (edu?.institution || edu?.school || '') : '',
        school: typeof edu === 'object' ? (edu?.school || edu?.institution || '') : '',
        startDate: typeof edu === 'object' ? (edu?.startDate || '') : '',
        endDate: typeof edu === 'object' ? (edu?.endDate || '') : ''
      })).filter((edu: any) => edu.degree || edu.title);
      
      const normalizedOriginalEducations = (cvData?.relevantEducations || []).map((edu: any) => ({
        degree: typeof edu === 'string' ? edu : (edu?.degree || edu?.title || ''),
        title: typeof edu === 'string' ? edu : (edu?.title || edu?.degree || ''),
        institution: typeof edu === 'object' ? (edu?.institution || edu?.school || '') : '',
        school: typeof edu === 'object' ? (edu?.school || edu?.institution || '') : '',
        startDate: typeof edu === 'object' ? (edu?.startDate || '') : '',
        endDate: typeof edu === 'object' ? (edu?.endDate || '') : ''
      })).filter((edu: any) => edu.degree || edu.title);
      
      if (JSON.stringify(normalizedCurrentEducations) !== JSON.stringify(normalizedOriginalEducations)) {
        updateData.relevantEducations = normalizedCurrentEducations;
      }
      // Normalisiere Branchen für den Vergleich
      const normalizedCurrentIndustries = relevantIndustries.map((industry: any) => ({
        industry: typeof industry === 'string' ? industry : (industry?.industry || industry?.name || ''),
        name: typeof industry === 'string' ? industry : (industry?.name || industry?.industry || ''),
        years: typeof industry === 'object' ? (industry?.years || '') : ''
      })).filter((industry: any) => industry.industry || industry.name);
      
      const normalizedOriginalIndustries = (cvData?.relevantIndustries || []).map((industry: any) => ({
        industry: typeof industry === 'string' ? industry : (industry?.industry || industry?.name || ''),
        name: typeof industry === 'string' ? industry : (industry?.name || industry?.industry || ''),
        years: typeof industry === 'object' ? (industry?.years || '') : ''
      })).filter((industry: any) => industry.industry || industry.name);
      
      if (JSON.stringify(normalizedCurrentIndustries) !== JSON.stringify(normalizedOriginalIndustries)) {
        updateData.relevantIndustries = normalizedCurrentIndustries;
      }
      
      if (Object.keys(updateData).length === 0) {
        setSuccess('Keine Änderungen zum Speichern');
        return;
      }
      
      console.log('💾 Sende Update-Daten:', updateData);
      console.log('💾 Update-Daten (optimizedWorkExperiences):', updateData.optimizedWorkExperiences);
      
      const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Speichern');
      }
      
      setSuccess('CV-Daten erfolgreich gespeichert');
      // Warte kurz, damit das Backend die Daten gespeichert hat
      await new Promise(resolve => setTimeout(resolve, 500));
      // Lade Daten neu, um aktualisierte Werte zu erhalten
      console.log('🔄 Lade Daten nach dem Speichern neu...');
      await loadCvData();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleApprove = async () => {
    try {
      setApproving(true);
      setError(null);
      
      const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Freigeben');
      }
      
      setSuccess('CV-Daten wurden erfolgreich freigegeben');
    } catch (err: any) {
      console.error('Fehler beim Freigeben:', err);
      setError('Fehler beim Freigeben: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setApproving(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          CV-Daten werden geladen...
        </Typography>
      </Container>
    );
  }
  
  if (error && isExpired) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Link abgelaufen</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Dieser Link ist abgelaufen und kann nicht mehr verwendet werden.
          </Typography>
        </Alert>
      </Container>
    );
  }
  
  if (error && !cvData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Fehler</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            CV-Informationen bearbeiten
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bitte überprüfen und passen Sie die CV-Informationen an. Sie können nur die optimierten Daten bearbeiten.
          </Typography>
          
          {expiresAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Gültig bis: {formatDate(expiresAt)}
              </Typography>
            </Box>
          )}
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        <Divider sx={{ my: 3 }} />
        
        {/* Optimiertes Kurzprofil */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kurzprofil
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={optimizedShortProfile}
            onChange={(e) => setOptimizedShortProfile(e.target.value)}
            variant="outlined"
            placeholder="Kurzprofil..."
            helperText={!optimizedShortProfile ? "Kein Kurzprofil vorhanden" : ""}
          />
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Kernkompetenzen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kernkompetenzen
          </Typography>
          {relevantCoreCompetencies.length > 0 ? (
            <>
              {relevantCoreCompetencies.map((comp, index) => {
                const competencyValue = typeof comp === 'string' ? comp : comp.competency || comp.name || '';
                const isEditing = editingCoreCompetencyIndex === index;
                
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEditing ? (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          value={competencyValue}
                          onChange={(e) => handleEditCoreCompetency(index, e.target.value)}
                          onBlur={() => setEditingCoreCompetencyIndex(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setEditingCoreCompetencyIndex(null);
                            }
                          }}
                          autoFocus
                        />
                      </>
                    ) : (
                      <>
                        <Typography 
                          variant="body1" 
                          sx={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => setEditingCoreCompetencyIndex(index)}
                        >
                          {competencyValue || 'Unbekannt'}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCoreCompetency(index)}
                          aria-label="Kernkompetenz löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Kernkompetenzen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Neue Kernkompetenz hinzufügen..."
              value={newCoreCompetency}
              onChange={(e) => setNewCoreCompetency(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddCoreCompetency();
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCoreCompetency}
              disabled={!newCoreCompetency.trim()}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        
        {/* IT-Skills */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            IT-Skills
          </Typography>
          {relevantItSkills.length > 0 ? (
            <>
              {relevantItSkills.map((skill, index) => {
                const skillName = typeof skill === 'string' ? skill : skill.name || skill.skill || '';
                const skillLevel = typeof skill === 'object' ? (skill.level || skill.proficiency || '') : '';
                const isEditing = editingItSkillIndex === index;
                
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Skill-Name"
                          value={skillName}
                          onChange={(e) => handleEditItSkill(index, 'skill', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Level (z.B. Fortgeschritten, Experte)"
                          value={skillLevel}
                          onChange={(e) => handleEditItSkill(index, 'level', e.target.value)}
                          placeholder="Optional"
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingItSkillIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setEditingItSkillIndex(index)}
                          >
                            {skillName || 'Unbekannt'}
                          </Typography>
                          {skillLevel && (
                            <Chip 
                              label={skillLevel} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              sx={{ width: 'fit-content' }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteItSkill(index)}
                          aria-label="IT-Skill löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine IT-Skills vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Skill-Name"
                placeholder="z.B. JavaScript, Python..."
                value={newItSkillName}
                onChange={(e) => setNewItSkillName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newItSkillName.trim()) {
                    handleAddItSkill();
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Level (optional)"
                placeholder="z.B. Fortgeschritten"
                value={newItSkillLevel}
                onChange={(e) => setNewItSkillLevel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newItSkillName.trim()) {
                    handleAddItSkill();
                  }
                }}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItSkill}
              disabled={!newItSkillName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Branchen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Branchen
          </Typography>
          {relevantIndustries.length > 0 ? (
            <>
              {relevantIndustries.map((industry, index) => {
                const industryName = industry.industry || industry.name || '';
                const industryYears = industry.years || '';
                const isEditing = editingIndustryIndex === index;
                
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Branche"
                          value={industryName}
                          onChange={(e) => handleEditIndustry(index, 'industry', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Jahre Erfahrung (optional)"
                          value={industryYears}
                          onChange={(e) => handleEditIndustry(index, 'years', e.target.value)}
                          placeholder="z.B. 5"
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingIndustryIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setEditingIndustryIndex(index)}
                          >
                            {industryName || 'Unbekannt'}
                          </Typography>
                          {industryYears && (
                            <Typography variant="body2" color="text.secondary">
                              {industryYears} Jahre Erfahrung
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteIndustry(index)}
                          aria-label="Branche löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Branchen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Branche"
                placeholder="z.B. IT, Finanzwesen..."
                value={newIndustryName}
                onChange={(e) => setNewIndustryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newIndustryName.trim()) {
                    handleAddIndustry();
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Jahre Erfahrung (optional)"
                placeholder="z.B. 5"
                value={newIndustryYears}
                onChange={(e) => setNewIndustryYears(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newIndustryName.trim()) {
                    handleAddIndustry();
                  }
                }}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddIndustry}
              disabled={!newIndustryName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        
        {/* Sprachen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sprachen
          </Typography>
          {relevantLanguages.length > 0 ? (
            <>
              {relevantLanguages.map((lang, index) => {
                const langName = typeof lang === 'string' ? lang : lang.language || lang.name || '';
                const langLevel = typeof lang === 'object' ? (lang.level || lang.proficiency || '') : '';
                const isEditing = editingLanguageIndex === index;
                
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Sprache"
                          value={langName}
                          onChange={(e) => handleEditLanguage(index, 'language', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Level (z.B. Muttersprache, Fließend, Grundkenntnisse)"
                          value={langLevel}
                          onChange={(e) => handleEditLanguage(index, 'level', e.target.value)}
                          placeholder="Optional"
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingLanguageIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setEditingLanguageIndex(index)}
                          >
                            {langName || 'Unbekannt'}
                          </Typography>
                          {langLevel && (
                            <Chip 
                              label={langLevel} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              sx={{ width: 'fit-content' }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLanguage(index)}
                          aria-label="Sprache löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Sprachen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Sprache"
                placeholder="z.B. Deutsch, Englisch..."
                value={newLanguageName}
                onChange={(e) => setNewLanguageName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newLanguageName.trim()) {
                    handleAddLanguage();
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Level (optional)"
                placeholder="z.B. Muttersprache"
                value={newLanguageLevel}
                onChange={(e) => setNewLanguageLevel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newLanguageName.trim()) {
                    handleAddLanguage();
                  }
                }}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLanguage}
              disabled={!newLanguageName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Berufserfahrungen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Berufserfahrungen
          </Typography>
          {optimizedWorkExperiences.length > 0 ? (
            <>
              {optimizedWorkExperiences.map((exp, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Position"
                    value={exp.position || exp.title || ''}
                    onChange={(e) => {
                      const updated = [...optimizedWorkExperiences];
                      updated[index] = { ...updated[index], position: e.target.value, title: e.target.value };
                      setOptimizedWorkExperiences(updated);
                    }}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Unternehmen"
                    value={exp.company || exp.employer || ''}
                    onChange={(e) => {
                      const updated = [...optimizedWorkExperiences];
                      updated[index] = { ...updated[index], company: e.target.value, employer: e.target.value };
                      setOptimizedWorkExperiences(updated);
                    }}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Von"
                      value={exp.startDate || exp.period?.split('-')[0]?.trim() || ''}
                      onChange={(e) => {
                        const updated = [...optimizedWorkExperiences];
                        updated[index] = { ...updated[index], startDate: e.target.value };
                        setOptimizedWorkExperiences(updated);
                      }}
                      variant="outlined"
                      placeholder="z.B. 01.2020"
                    />
                    <TextField
                      fullWidth
                      label="Bis"
                      value={exp.endDate || exp.period?.split('-')[1]?.trim() || ''}
                      onChange={(e) => {
                        const updated = [...optimizedWorkExperiences];
                        updated[index] = { ...updated[index], endDate: e.target.value };
                        setOptimizedWorkExperiences(updated);
                      }}
                      variant="outlined"
                      placeholder="z.B. 12.2023 oder 'heute'"
                    />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Beschreibung"
                    value={exp.description || ''}
                    onChange={(e) => {
                      const updated = [...optimizedWorkExperiences];
                      updated[index] = { ...updated[index], description: e.target.value };
                      setOptimizedWorkExperiences(updated);
                    }}
                    variant="outlined"
                  />
                </Paper>
              ))}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Keine Berufserfahrungen vorhanden.
            </Typography>
          )}
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Ausbildungen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Ausbildungen
          </Typography>
          {relevantEducations.length > 0 ? (
            <>
              {relevantEducations.map((edu, index) => {
                const isEditing = editingEducationIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Abschluss"
                          value={edu.degree || edu.title || ''}
                          onChange={(e) => handleEditEducation(index, 'degree', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Institution"
                          value={edu.institution || edu.school || ''}
                          onChange={(e) => handleEditEducation(index, 'institution', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Von (optional)"
                            value={edu.startDate || ''}
                            onChange={(e) => handleEditEducation(index, 'startDate', e.target.value)}
                            placeholder="z.B. 2015"
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Bis (optional)"
                            value={edu.endDate || ''}
                            onChange={(e) => handleEditEducation(index, 'endDate', e.target.value)}
                            placeholder="z.B. 2019"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingEducationIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingEducationIndex(index)}>
                            {edu.degree || edu.title || 'Abschluss'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {edu.institution || edu.school || 'Institution'}
                          </Typography>
                          {(edu.startDate || edu.endDate) && (
                            <Typography variant="body2" color="text.secondary">
                              {edu.startDate || ''} {edu.endDate ? `- ${edu.endDate}` : ''}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteEducation(index)}
                          aria-label="Ausbildung löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Ausbildungen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Abschluss"
              placeholder="z.B. Bachelor of Science"
              value={newEducationDegree}
              onChange={(e) => setNewEducationDegree(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Institution (optional)"
              placeholder="z.B. Universität XYZ"
              value={newEducationInstitution}
              onChange={(e) => setNewEducationInstitution(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Von (optional)"
                placeholder="z.B. 2015"
                value={newEducationStartDate}
                onChange={(e) => setNewEducationStartDate(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Bis (optional)"
                placeholder="z.B. 2019"
                value={newEducationEndDate}
                onChange={(e) => setNewEducationEndDate(e.target.value)}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddEducation}
              disabled={!newEducationDegree.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Zertifikate */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Zertifikate
          </Typography>
          {relevantCertifications.length > 0 ? (
            <>
              {relevantCertifications.map((cert, index) => {
                const isEditing = editingCertificationIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Zertifikat"
                          value={cert.name || cert.certificate || ''}
                          onChange={(e) => handleEditCertification(index, 'name', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Aussteller (optional)"
                          value={cert.issuer || ''}
                          onChange={(e) => handleEditCertification(index, 'issuer', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Datum (optional)"
                          value={cert.date || ''}
                          onChange={(e) => handleEditCertification(index, 'date', e.target.value)}
                          placeholder="z.B. 2023"
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingCertificationIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingCertificationIndex(index)}>
                            {cert.name || cert.certificate || 'Zertifikat'}
                          </Typography>
                          {cert.issuer && (
                            <Typography variant="body2" color="text.secondary">
                              {cert.issuer}
                            </Typography>
                          )}
                          {cert.date && (
                            <Typography variant="body2" color="text.secondary">
                              {cert.date}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCertification(index)}
                          aria-label="Zertifikat löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Zertifikate vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Zertifikat"
              placeholder="z.B. AWS Certified Solutions Architect"
              value={newCertificationName}
              onChange={(e) => setNewCertificationName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Aussteller (optional)"
              placeholder="z.B. Amazon Web Services"
              value={newCertificationIssuer}
              onChange={(e) => setNewCertificationIssuer(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Datum (optional)"
              placeholder="z.B. 2023"
              value={newCertificationDate}
              onChange={(e) => setNewCertificationDate(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCertification}
              disabled={!newCertificationName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        
        {/* Hobbys */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hobbys
          </Typography>
          {relevantHobbies.length > 0 ? (
            <>
              {relevantHobbies.map((hobby, index) => {
                const isEditing = editingHobbyIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hobby"
                          value={hobby.hobby || hobby.name || ''}
                          onChange={(e) => handleEditHobby(index, 'hobby', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          label="Beschreibung (optional)"
                          value={hobby.description || ''}
                          onChange={(e) => handleEditHobby(index, 'description', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingHobbyIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingHobbyIndex(index)}>
                            {hobby.hobby || hobby.name || 'Hobby'}
                          </Typography>
                          {hobby.description && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {hobby.description}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteHobby(index)}
                          aria-label="Hobby löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Hobbys vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Hobby"
              placeholder="z.B. Fotografie, Lesen..."
              value={newHobbyName}
              onChange={(e) => setNewHobbyName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Beschreibung (optional)"
              placeholder="z.B. Leidenschaft für Landschaftsfotografie"
              value={newHobbyDescription}
              onChange={(e) => setNewHobbyDescription(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddHobby}
              disabled={!newHobbyName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        
        {/* Zusätzliche Informationen */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Zusätzliche Informationen
          </Typography>
          {relevantAdditionalInfo.length > 0 ? (
            <>
              {relevantAdditionalInfo.map((info, index) => {
                const isEditing = editingAdditionalInfoIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Information"
                          value={info.info || ''}
                          onChange={(e) => handleEditAdditionalInfo(index, 'info', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          label="Details (optional)"
                          value={info.details || ''}
                          onChange={(e) => handleEditAdditionalInfo(index, 'details', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingAdditionalInfoIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingAdditionalInfoIndex(index)}>
                            {info.info || 'Information'}
                          </Typography>
                          {info.details && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {info.details}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAdditionalInfo(index)}
                          aria-label="Information löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine zusätzlichen Informationen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Information"
              placeholder="z.B. Führerschein, Ehrenamt..."
              value={newAdditionalInfoInfo}
              onChange={(e) => setNewAdditionalInfoInfo(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Details (optional)"
              placeholder="z.B. Klasse B, seit 2010"
              value={newAdditionalInfoDetails}
              onChange={(e) => setNewAdditionalInfoDetails(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAdditionalInfo}
              disabled={!newAdditionalInfoInfo.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={saving || isExpired}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Speichern...' : 'Änderungen speichern'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approving || isExpired}
            startIcon={approving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {approving ? 'Freigeben...' : 'CV-Daten freigeben'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CvSharePage;

